import { createContext, useContext, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingBasket, X, Minus, Plus, Trash2, Send, CheckCircle2, Loader2 } from "lucide-react";
import { submitEnquiry, formatApiError } from "@/lib/api";

const EnquiryContext = createContext(null);
export const useEnquiry = () => useContext(EnquiryContext);

export function EnquiryProvider({ children }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const addItem = (p) =>
    setItems((prev) =>
      prev.find((i) => i.id === p.id)
        ? prev.map((i) => (i.id === p.id ? { ...i, qty: i.qty + 1 } : i))
        : [...prev, { id: p.id, name: p.name, price: p.price, unit: p.unit, qty: 1 }]
    );
  const removeItem = (id) => setItems((prev) => prev.filter((i) => i.id !== id));
  const updateQty = (id, qty) =>
    setItems((prev) =>
      qty <= 0 ? prev.filter((i) => i.id !== id) : prev.map((i) => (i.id === id ? { ...i, qty } : i))
    );
  const clear = () => setItems([]);
  const count = items.reduce((a, i) => a + i.qty, 0);

  const value = useMemo(
    () => ({ items, addItem, removeItem, updateQty, clear, count, open, setOpen }),
    [items, open, count]
  );
  return <EnquiryContext.Provider value={value}>{children}</EnquiryContext.Provider>;
}

export function FloatingCartButton() {
  const { count, setOpen } = useEnquiry();
  return (
    <AnimatePresence>
      {count > 0 && (
        <motion.button
          initial={{ opacity: 0, scale: 0.6, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.6, y: 20 }}
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2.5 rounded-full bg-marigold text-white pl-5 pr-6 py-3.5 shadow-2xl shadow-marigold/40 hover:bg-marigold-bright transition-colors duration-300"
          data-testid="floating-cart-button"
        >
          <ShoppingBasket className="w-5 h-5" />
          <span className="text-sm font-bold">{count} item{count > 1 ? "s" : ""} · Enquire now</span>
        </motion.button>
      )}
    </AnimatePresence>
  );
}

export function EnquiryDrawer() {
  const { items, open, setOpen, updateQty, removeItem, clear, count } = useEnquiry();
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [note, setNote] = useState("");
  const [state, setState] = useState({ status: "idle", error: "" });

  const total = items.reduce((a, i) => a + i.price * i.qty, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setState({ status: "loading", error: "" });
    try {
      await submitEnquiry({
        name,
        mobile,
        category: "Enquiry Cart",
        message: note || undefined,
        items: items.map((i) => ({ name: i.name, qty: i.qty, unit: i.unit, price: i.price })),
      });
      setState({ status: "success", error: "" });
      clear();
    } catch (err) {
      setState({ status: "error", error: formatApiError(err) });
    }
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => setState({ status: "idle", error: "" }), 400);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[60] bg-ink/50 backdrop-blur-sm"
            data-testid="cart-drawer-backdrop"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 260 }}
            className="fixed top-0 right-0 z-[70] h-full w-full max-w-md bg-bone shadow-2xl flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-line bg-cream">
              <div>
                <h3 className="font-serif font-bold text-xl text-ink">Your Enquiry List</h3>
                <p className="text-xs text-ink-muted">{count} item{count !== 1 ? "s" : ""} · approx. ₹{total}</p>
              </div>
              <button onClick={close} className="p-2 rounded-full hover:bg-line/60 transition-colors" data-testid="cart-drawer-close" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {state.status === "success" ? (
              <div className="flex-1 flex flex-col items-center justify-center px-8 text-center" data-testid="cart-success-panel">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", damping: 12 }}>
                  <CheckCircle2 className="w-16 h-16 text-forest" />
                </motion.div>
                <h4 className="mt-5 font-serif font-bold text-2xl text-ink">Enquiry sent!</h4>
                <p className="mt-3 text-sm text-ink-soft leading-relaxed">
                  Dhanyavaad! We have received your list and will call you back shortly to confirm
                  availability and delivery time.
                </p>
                <button
                  onClick={close}
                  className="mt-8 rounded-full bg-forest text-bone px-7 py-3 text-sm font-semibold hover:bg-forest-deep transition-colors"
                  data-testid="cart-success-close-button"
                >
                  Continue Browsing
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  {items.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center" data-testid="cart-empty-state">
                      <ShoppingBasket className="w-12 h-12 text-line" />
                      <p className="mt-4 font-serif text-lg text-ink-soft">Your enquiry list is empty</p>
                      <p className="mt-1 text-xs text-ink-muted">Add products from the catalog to enquire together.</p>
                    </div>
                  ) : (
                    <ul className="divide-y divide-line">
                      {items.map((i) => (
                        <li key={i.id} className="py-4 flex items-center gap-3" data-testid={`cart-item-${i.id}`}>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-ink truncate">{i.name}</p>
                            <p className="text-xs text-ink-muted">₹{i.price} · {i.unit}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => updateQty(i.id, i.qty - 1)} className="w-7 h-7 rounded-full border border-line flex items-center justify-center hover:bg-cream" data-testid={`cart-qty-minus-${i.id}`} aria-label="Decrease">
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="w-6 text-center text-sm font-bold">{i.qty}</span>
                            <button onClick={() => updateQty(i.id, i.qty + 1)} className="w-7 h-7 rounded-full border border-line flex items-center justify-center hover:bg-cream" data-testid={`cart-qty-plus-${i.id}`} aria-label="Increase">
                              <Plus className="w-3 h-3" />
                            </button>
                            <button onClick={() => removeItem(i.id)} className="p-1.5 text-ink-muted hover:text-red-600" data-testid={`cart-remove-${i.id}`} aria-label="Remove">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {items.length > 0 && (
                  <form onSubmit={handleSubmit} className="border-t border-line bg-cream px-6 py-5 space-y-3">
                    <input
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your full name"
                      className="w-full rounded-xl border border-line bg-white px-4 py-3 text-sm outline-none focus:border-forest focus:ring-2 focus:ring-forest/15"
                      data-testid="cart-name-input"
                    />
                    <input
                      required
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                      placeholder="Mobile number (WhatsApp preferred)"
                      pattern="[0-9+ -]{10,15}"
                      className="w-full rounded-xl border border-line bg-white px-4 py-3 text-sm outline-none focus:border-forest focus:ring-2 focus:ring-forest/15"
                      data-testid="cart-mobile-input"
                    />
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Any note — brand preference, delivery time… (optional)"
                      rows={2}
                      className="w-full rounded-xl border border-line bg-white px-4 py-3 text-sm outline-none focus:border-forest focus:ring-2 focus:ring-forest/15 resize-none"
                      data-testid="cart-note-input"
                    />
                    {state.status === "error" && (
                      <p className="text-xs text-red-600" data-testid="cart-error-message">{state.error}</p>
                    )}
                    <button
                      type="submit"
                      disabled={state.status === "loading"}
                      className="w-full flex items-center justify-center gap-2 rounded-full bg-forest text-bone py-3.5 text-sm font-bold hover:bg-forest-deep transition-colors disabled:opacity-60"
                      data-testid="cart-submit-button"
                    >
                      {state.status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Request These Items · ₹{total} approx.
                    </button>
                    <p className="text-[11px] text-center text-ink-muted">No advance payment — pay on delivery or at the counter.</p>
                  </form>
                )}
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
